/**
 * Expense Management Page
 * Expense capture, categorization, and approval workflows
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Receipt, Plus, Search, Filter, Clock, CheckCircle, XCircle, 
  AlertCircle, Upload, Loader2, RefreshCcw, Download, Eye, 
  ThumbsUp, ThumbsDown, FileText, DollarSign, Calendar, Building2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import { toast } from 'sonner';

// Expense categories
const EXPENSE_CATEGORIES = [
  { value: 'food_supplies', label: 'Food Supplies' },
  { value: 'beverages', label: 'Beverages' },
  { value: 'kitchen_equipment', label: 'Kitchen Equipment' },
  { value: 'cleaning', label: 'Cleaning & Maintenance' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'marketing', label: 'Marketing & Advertising' },
  { value: 'staff', label: 'Staff Related' },
  { value: 'rent', label: 'Rent & Lease' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'repairs', label: 'Repairs & Maintenance' },
  { value: 'office', label: 'Office Supplies' },
  { value: 'travel', label: 'Travel & Transportation' },
  { value: 'other', label: 'Other' },
];

// Payment methods
const PAYMENT_METHODS = [
  { value: 'card', label: 'Company Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'petty_cash', label: 'Petty Cash' },
];

// Status colors
const STATUS_STYLES = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700', icon: FileText },
  pending_approval: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  paid: { bg: 'bg-blue-100', text: 'text-blue-700', icon: DollarSign },
};

export default function ExpenseManagementPage() {
  const { currentOutlet, outletId, user, tenantId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('expenses');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [expenseForm, setExpenseForm] = useState({
    amount: '',
    category: '',
    vendor_name: '',
    description: '',
    payment_method: 'card',
    expense_date: new Date().toISOString().split('T')[0],
    receipt_number: '',
    notes: '',
  });

  // Fetch expenses
  const fetchExpenses = useCallback(async () => {
    if (!outletId) return;
    
    setLoading(true);
    try {
      const response = await api.get('/finance/expenses', {
        params: { outlet_id: outletId, limit: 100 }
      });
      setExpenses(response.data?.expenses || response.data || []);
    } catch (err) {
      console.error('Failed to fetch expenses:', err);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  // Fetch pending approvals
  const fetchPendingApprovals = useCallback(async () => {
    try {
      const response = await api.get('/finance/expenses', {
        params: { status: 'pending_approval' }
      });
      setPendingApprovals(response.data || []);
    } catch (err) {
      console.error('Failed to fetch approvals:', err);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
    fetchPendingApprovals();
  }, [fetchExpenses, fetchPendingApprovals]);

  // Stats calculations
  const stats = {
    total: expenses.length,
    pending: expenses.filter(e => e.status === 'pending_approval').length,
    approved: expenses.filter(e => e.status === 'approved' || e.status === 'paid').length,
    totalAmount: expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
    pendingAmount: expenses
      .filter(e => e.status === 'pending_approval')
      .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = 
      expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || expense.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Create expense
  const handleCreateExpense = async (submitForApproval = false) => {
    if (!expenseForm.amount || !expenseForm.category || !expenseForm.description) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post('/finance/expenses', {
        tenant_id: tenantId,
        outlet_id: outletId,
        amount: parseFloat(expenseForm.amount),
        currency: 'NOK',
        category: expenseForm.category,
        vendor_name: expenseForm.vendor_name,
        description: expenseForm.description,
        payment_method: expenseForm.payment_method,
        expense_date: expenseForm.expense_date,
        receipt_number: expenseForm.receipt_number,
        notes: expenseForm.notes,
        status: submitForApproval ? 'pending_approval' : 'draft',
      });
      
      toast.success(submitForApproval ? 'Expense submitted for approval' : 'Expense saved as draft');
      setCreateDialogOpen(false);
      setExpenseForm({
        amount: '',
        category: '',
        vendor_name: '',
        description: '',
        payment_method: 'card',
        expense_date: new Date().toISOString().split('T')[0],
        receipt_number: '',
        notes: '',
      });
      fetchExpenses();
      fetchPendingApprovals();
    } catch (err) {
      toast.error('Failed to create expense');
    } finally {
      setSubmitting(false);
    }
  };

  // Approve expense
  const handleApprove = async (expenseId) => {
    setSubmitting(true);
    try {
      await api.put(`/finance/expenses/${expenseId}`, {
        status: 'approved',
        approved_by: user?.id,
        approved_at: new Date().toISOString(),
      });
      
      toast.success('Expense approved');
      fetchExpenses();
      fetchPendingApprovals();
      setViewDialogOpen(false);
    } catch (err) {
      toast.error('Failed to approve expense');
    } finally {
      setSubmitting(false);
    }
  };

  // Reject expense
  const handleReject = async (expenseId, reason = '') => {
    setSubmitting(true);
    try {
      await api.put(`/finance/expenses/${expenseId}`, {
        status: 'rejected',
        rejection_reason: reason || 'Rejected by approver',
      });
      
      toast.success('Expense rejected');
      fetchExpenses();
      fetchPendingApprovals();
      setViewDialogOpen(false);
    } catch (err) {
      toast.error('Failed to reject expense');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.draft;
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} ${style.text} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl text-gray-900">Expense Management</h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">
            {currentOutlet?.name || 'All Outlets'} - Track, submit, and approve expenses
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchExpenses(); fetchPendingApprovals(); }}>
            <RefreshCcw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button 
            className="bg-orange-500 hover:bg-orange-600" 
            size="sm"
            onClick={() => setCreateDialogOpen(true)}
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Expense</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Expenses</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-sm text-gray-500">Pending Approval</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.approved}</p>
                <p className="text-sm text-gray-500">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalAmount)}</p>
                <p className="text-sm text-gray-500">Total Amount</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="expenses" data-testid="tab-expenses">
            All Expenses
          </TabsTrigger>
          <TabsTrigger value="approvals" data-testid="tab-approvals">
            Pending Approvals
            {pendingApprovals.length > 0 && (
              <Badge className="ml-2 bg-amber-500">{pendingApprovals.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* All Expenses Tab */}
        <TabsContent value="expenses" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search expenses..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="expense-search"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="pending_approval">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {EXPENSE_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : filteredExpenses.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No expenses found</p>
                  <Button 
                    variant="link" 
                    className="mt-2"
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    Add your first expense
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="pb-3 font-medium text-gray-600">Date</th>
                        <th className="pb-3 font-medium text-gray-600">Description</th>
                        <th className="pb-3 font-medium text-gray-600">Category</th>
                        <th className="pb-3 font-medium text-gray-600">Vendor</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Amount</th>
                        <th className="pb-3 font-medium text-gray-600">Status</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredExpenses.map((expense) => (
                        <tr key={expense.id} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="py-3 text-sm">
                            {new Date(expense.expense_date).toLocaleDateString()}
                          </td>
                          <td className="py-3">
                            <div className="font-medium">{expense.description}</div>
                            {expense.receipt_number && (
                              <div className="text-xs text-gray-500">#{expense.receipt_number}</div>
                            )}
                          </td>
                          <td className="py-3 text-gray-600 text-sm">
                            {EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}
                          </td>
                          <td className="py-3 text-gray-600 text-sm">{expense.vendor_name || '-'}</td>
                          <td className="py-3 text-right font-mono font-medium">
                            {formatCurrency(expense.amount)}
                          </td>
                          <td className="py-3">{getStatusBadge(expense.status)}</td>
                          <td className="py-3">
                            <div className="flex justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0"
                                onClick={() => {
                                  setSelectedExpense(expense);
                                  setViewDialogOpen(true);
                                }}
                                data-testid={`view-expense-${expense.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approvals Tab */}
        <TabsContent value="approvals" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Approvals</CardTitle>
              <CardDescription>Expenses waiting for your approval</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingApprovals.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                  <p>No pending approvals</p>
                  <p className="text-sm">All caught up!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingApprovals.map((expense) => (
                    <div
                      key={expense.id}
                      className="flex items-center gap-4 p-4 rounded-lg border hover:shadow-md transition-shadow"
                    >
                      <div className="p-3 bg-amber-100 rounded-full">
                        <Receipt className="w-6 h-6 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{expense.description}</p>
                          <Badge variant="outline" className="text-xs">
                            {EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(expense.expense_date).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {expense.outlet_name || 'Unknown Outlet'}
                          </span>
                          {expense.vendor_name && (
                            <span>Vendor: {expense.vendor_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-900">
                          {formatCurrency(expense.amount)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Submitted by {expense.created_by_name || 'Staff'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleApprove(expense.id)}
                          disabled={submitting}
                          data-testid={`approve-${expense.id}`}
                        >
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleReject(expense.id)}
                          disabled={submitting}
                          data-testid={`reject-${expense.id}`}
                        >
                          <ThumbsDown className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Expense Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-orange-600" />
              Add New Expense
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (NOK) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  data-testid="expense-amount"
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={expenseForm.expense_date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Category *</Label>
              <Select 
                value={expenseForm.category} 
                onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}
              >
                <SelectTrigger data-testid="expense-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Input
                placeholder="What was this expense for?"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                data-testid="expense-description"
              />
            </div>
            
            <div>
              <Label>Vendor Name</Label>
              <Input
                placeholder="Supplier/vendor name"
                value={expenseForm.vendor_name}
                onChange={(e) => setExpenseForm({ ...expenseForm, vendor_name: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Method</Label>
                <Select 
                  value={expenseForm.payment_method} 
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, payment_method: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(method => (
                      <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Receipt Number</Label>
                <Input
                  placeholder="Receipt #"
                  value={expenseForm.receipt_number}
                  onChange={(e) => setExpenseForm({ ...expenseForm, receipt_number: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Any additional notes..."
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="outline"
              onClick={() => handleCreateExpense(false)} 
              disabled={submitting}
            >
              Save as Draft
            </Button>
            <Button 
              onClick={() => handleCreateExpense(true)} 
              disabled={submitting}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="submit-expense"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Expense Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-blue-600" />
              Expense Details
            </DialogTitle>
          </DialogHeader>
          {selectedExpense && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(selectedExpense.amount)}</p>
                  <p className="text-gray-500">{selectedExpense.description}</p>
                </div>
                {getStatusBadge(selectedExpense.status)}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Category</p>
                  <p className="font-medium">
                    {EXPENSE_CATEGORIES.find(c => c.value === selectedExpense.category)?.label}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">
                    {new Date(selectedExpense.expense_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Vendor</p>
                  <p className="font-medium">{selectedExpense.vendor_name || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Payment Method</p>
                  <p className="font-medium">
                    {PAYMENT_METHODS.find(m => m.value === selectedExpense.payment_method)?.label || '-'}
                  </p>
                </div>
                {selectedExpense.receipt_number && (
                  <div>
                    <p className="text-gray-500">Receipt #</p>
                    <p className="font-medium">{selectedExpense.receipt_number}</p>
                  </div>
                )}
              </div>
              
              {selectedExpense.notes && (
                <div>
                  <p className="text-gray-500 text-sm">Notes</p>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg mt-1">{selectedExpense.notes}</p>
                </div>
              )}
              
              {selectedExpense.status === 'pending_approval' && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => handleApprove(selectedExpense.id)}
                    disabled={submitting}
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 text-red-600 hover:bg-red-50"
                    onClick={() => handleReject(selectedExpense.id)}
                    disabled={submitting}
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
